import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CreditCard, CheckCircle, AlertTriangle, ExternalLink, Eye, EyeOff, Shield, HelpCircle, BookOpen, Sparkles } from "lucide-react";
import QuickStartPagamentoDialog from "@/components/QuickStartPagamentoDialog";

export default function ConfiguracoesPagamento({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ configured: false });
  const [showToken, setShowToken] = useState(false);
  const [quickStartOpen, setQuickStartOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    mercadopago_access_token: "",
    mercadopago_public_key: ""
  });

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await api.get('/perfil/mercadopago/status');
      setStatus(response.data);
    } catch (error) {
      console.error("Erro ao carregar status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/perfil/mercadopago', formData);
      toast.success("Configurações salvas com sucesso!");
      setFormData({ mercadopago_access_token: "", mercadopago_public_key: "" });
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar configurações");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm("Tem certeza que deseja remover as credenciais do Mercado Pago?")) {
      return;
    }
    
    setSaving(true);
    try {
      await api.put('/perfil/mercadopago', {
        mercadopago_access_token: "",
        mercadopago_public_key: ""
      });
      toast.success("Credenciais removidas");
      fetchStatus();
    } catch (error) {
      toast.error("Erro ao remover credenciais");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Layout onLogout={onLogout}><div className="text-center py-12">Carregando...</div></Layout>;
  }

  return (
    <Layout onLogout={onLogout}>
      <QuickStartPagamentoDialog open={quickStartOpen} onOpenChange={setQuickStartOpen} />
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>
              Configurações de Pagamento
            </h1>
            <p className="text-gray-600">Configure sua conta do Mercado Pago para receber pagamentos</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setQuickStartOpen(true)}
              data-testid="btn-quickstart-pagamento"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              <Sparkles className="w-4 h-4 mr-1.5" />
              Início rápido
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/ajuda/pagamentos')}
              data-testid="btn-manual-pagamento"
            >
              <BookOpen className="w-4 h-4 mr-1.5" />
              Manual completo
            </Button>
          </div>
        </div>

        {/* Status atual */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Status do Mercado Pago
            </CardTitle>
          </CardHeader>
          <CardContent>
            {status.configured ? (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <strong>Mercado Pago configurado!</strong>
                      <p className="text-sm mt-1">Você pode receber pagamentos via PIX, Boleto e Cartão.</p>
                    </div>
                    <Badge className="bg-green-600">Ativo</Badge>
                  </div>
                </AlertDescription>
              </Alert>
            ) : (
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  <strong>Mercado Pago não configurado</strong>
                  <p className="text-sm mt-1">Configure suas credenciais abaixo para começar a receber pagamentos.</p>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Formulário de configuração */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              {status.configured ? "Atualizar Credenciais" : "Configurar Credenciais"}
            </CardTitle>
            <CardDescription>
              Suas credenciais são armazenadas de forma segura e usadas apenas para processar pagamentos.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="access_token">Access Token *</Label>
              <div className="relative">
                <Input
                  id="access_token"
                  type={showToken ? "text" : "password"}
                  value={formData.mercadopago_access_token}
                  onChange={(e) => setFormData({ ...formData, mercadopago_access_token: e.target.value })}
                  placeholder="APP_USR-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowToken(!showToken)}
                >
                  {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Encontre seu Access Token no painel de desenvolvedor do Mercado Pago
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="public_key">Public Key (opcional)</Label>
              <Input
                id="public_key"
                type="text"
                value={formData.mercadopago_public_key}
                onChange={(e) => setFormData({ ...formData, mercadopago_public_key: e.target.value })}
                placeholder="APP_USR-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              />
              <p className="text-xs text-gray-500">
                A Public Key é usada para pagamentos transparentes (opcional)
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button 
                onClick={handleSave} 
                disabled={saving || !formData.mercadopago_access_token}
                className="flex-1"
              >
                {saving ? "Salvando..." : "Salvar Configurações"}
              </Button>
              {status.configured && (
                <Button 
                  variant="outline" 
                  onClick={handleRemove}
                  disabled={saving}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  Remover
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Como obter as credenciais */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5" />
              Como obter suas credenciais?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ol className="list-decimal list-inside space-y-3 text-sm">
              <li>
                Acesse o painel de desenvolvedores do Mercado Pago:
                <Button
                  variant="link"
                  className="h-auto p-0 ml-1"
                  onClick={() => window.open('https://www.mercadopago.com.br/developers/panel/app', '_blank')}
                >
                  Acessar Painel <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </li>
              <li>Faça login com sua conta do Mercado Pago (ou crie uma)</li>
              <li>Clique em "Criar aplicação" (se ainda não tiver)</li>
              <li>Acesse "Credenciais de produção" ou "Credenciais de teste"</li>
              <li>Copie o <strong>Access Token</strong> e cole no campo acima</li>
            </ol>

            <Alert className="bg-blue-50 border-blue-200">
              <AlertDescription className="text-blue-800 text-sm">
                <strong>Dica:</strong> Para testar, você pode usar as credenciais de teste primeiro. 
                Quando estiver pronto para receber pagamentos reais, use as credenciais de produção.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
